/**
 * Bricktopia Price Check System
 * 
 * Для кожного товару в кошику:
 * 1. Визначає тип (design / frame / pet)
 * 2. Отримує актуальну ціну з API
 * 3. Порівнює з ціною в кошику
 * 4. Якщо ціна змінилась — шукає відповідний variant через бекенд
 * 5. Логує результат у консоль
 */
(function() {
  'use strict';

  // =============================================
  // КОНФІГУРАЦІЯ
  // =============================================

  // KILL SWITCH — set to true to fully disable the cart price check.
  // While disabled: no API calls, no "Updating prices…" overlay, no auto-reload.
  // Reason: backend variant-by-price endpoint can return errors like
  // "The variant 'bricktopia_price_XXXX' already exists" which previously hung the page.
  // Re-enable by setting this to false once the backend issue is resolved.
  var DISABLED = true;

  var PRICE_API_BASE = 'https://bricktopia-constructor.vercel.app/api/price';
  var BACKEND_API_BASE = 'https://api.bricktopia.store';

  // =============================================
  // ВИЗНАЧЕННЯ ТИПУ ТОВАРУ (та сама логіка що в BricktopiaCart)
  // =============================================

  function findJsonConfig(cartItem) {
    if (!cartItem || !cartItem.properties) return null;

    /* `_`-імена — нові (приховані від покупця), без `_` — старі кошики */
    var externalFigureConfigId = cartItem.properties._figure_config_id || cartItem.properties.figure_config_id || null;

    var keys = Object.keys(cartItem.properties);
    for (var k = 0; k < keys.length; k++) {
      var key = keys[k];
      var value = cartItem.properties[key];
      if (!value || typeof value !== 'string') continue;

      if (value.indexOf('figureConfigId') !== -1 || value.indexOf('imageUrls') !== -1 ||
          value.indexOf('frameOrderId') !== -1 || (value.indexOf('"type"') !== -1 && value.indexOf('"frame"') !== -1) ||
          (value.indexOf('"preview"') !== -1 && value.indexOf('"id"') !== -1) ||
          (value.indexOf('"price"') !== -1 && value.indexOf('"id"') !== -1)) {
        try {
          var parsed = JSON.parse(value);

          if (parsed.figureConfigId || parsed.imageUrls) {
            return { config: parsed, configType: 'figure' };
          }

          if (parsed.frameOrderId || parsed.type === 'frame') {
            return { config: parsed, configType: 'frame' };
          }

          if (parsed.id !== undefined && parsed.id !== null && (parsed.preview || parsed.price !== undefined)) {
            var fullTitle = (cartItem.product_title || cartItem.title || '').toLowerCase();

            if (fullTitle.indexOf('звірят') !== -1 || fullTitle.indexOf('pet') !== -1 ||
                fullTitle.indexOf('zwierzą') !== -1 || fullTitle.indexOf('figurka zwierzątka') !== -1) {
              return { config: parsed, configType: 'pet' };
            }

            if ('keychain' in parsed || externalFigureConfigId) {
              if (externalFigureConfigId && !parsed.figureConfigId) {
                parsed._externalFigureConfigId = externalFigureConfigId;
              }
              return { config: parsed, configType: 'figure' };
            }

            if (fullTitle.indexOf('рамк') !== -1 || fullTitle.indexOf('frame') !== -1 || fullTitle.indexOf('ramk') !== -1 ||
                parsed.delayDays !== undefined || parsed.frameSize || parsed.figuresCount) {
              return { config: parsed, configType: 'frame' };
            }

            if (fullTitle.indexOf('фігурк') !== -1 || fullTitle.indexOf('figurine') !== -1 || fullTitle.indexOf('figurk') !== -1 ||
                fullTitle.indexOf('брелок') !== -1 || fullTitle.indexOf('keychain') !== -1 || fullTitle.indexOf('brelok') !== -1) {
              return { config: parsed, configType: 'figure' };
            }

            return { config: parsed, configType: 'frame' };
          }
        } catch (e) { /* не JSON */ }
      }
    }
    return null;
  }

  /**
   * ID для price API
   */
  function getApiId(config, configType) {
    if (configType === 'frame') {
      return config.frameOrderId !== undefined ? config.frameOrderId : config.id;
    }
    if (configType === 'pet') {
      return config.id;
    }
    return config.figureConfigId !== undefined
      ? config.figureConfigId
      : (config._externalFigureConfigId || config.id);
  }

  /**
   * configType → type для price API
   */
  function getApiType(configType) {
    if (configType === 'figure') return 'design';
    if (configType === 'frame') return 'frame';
    if (configType === 'pet') return 'pet';
    return configType;
  }

  /**
   * configType → productType для variant-by-price
   *   frame → "frame"
   *   pet   → "pet"
   *   figure/інше → "mini"
   */
  function getProductType(configType) {
    if (configType === 'frame') return 'frame';
    if (configType === 'pet') return 'pet';
    return 'mini';
  }

  // =============================================
  // API ЗАПИТИ
  // =============================================

  async function fetchPrice(apiType, apiId) {
    var url = PRICE_API_BASE + '?type=' + encodeURIComponent(apiType) + '&id=' + encodeURIComponent(apiId);
    var response = await fetch(url);
    if (!response.ok) throw new Error('HTTP ' + response.status);
    return await response.json();
  }

  async function fetchVariantByPrice(price, productType) {
    var url = BACKEND_API_BASE + '/shopify/variant-by-price';
    var response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ price: price, productType: productType })
    });
    if (!response.ok) throw new Error('HTTP ' + response.status);
    return await response.json();
  }

  /**
   * Витягує числовий ID з Shopify GID (gid://shopify/ProductVariant/123 → 123)
   */
  function extractNumericId(gid) {
    if (typeof gid === 'number') return gid;
    if (typeof gid === 'string' && gid.indexOf('gid://') === 0) {
      return gid.split('/').pop();
    }
    return gid;
  }

  // =============================================
  // LOADING OVERLAY
  // =============================================

  function showLoadingOverlay(total) {
    if (document.querySelector('.bricktopia-pricecheck-overlay')) return;

    var style = document.createElement('style');
    style.textContent = '@keyframes bricktopia-pc-spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);

    var overlay = document.createElement('div');
    overlay.className = 'bricktopia-pricecheck-overlay';
    overlay.setAttribute('style',
      'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(255,255,255,0.88);'
      + 'display:flex;align-items:center;justify-content:center;z-index:99999;'
    );
    overlay.innerHTML = '<div style="display:flex;flex-direction:column;align-items:center;gap:12px;text-align:center;">'
      + '<div style="width:40px;height:40px;border:4px solid #e0d6cc;border-top-color:#8B4513;border-radius:50%;animation:bricktopia-pc-spin 0.8s linear infinite;"></div>'
      + '<p style="font-family:Rubik,sans-serif;font-size:16px;color:#67361C;font-weight:600;margin:0;">Updating prices…</p>'
      + '<p style="font-family:Rubik,sans-serif;font-size:13px;color:#999;font-weight:400;margin:0;">Some prices have changed since you added items to cart.</p>'
      + '<p class="bricktopia-pc-counter" style="font-family:Rubik,sans-serif;font-size:14px;color:#8B4513;font-weight:600;margin:4px 0 0 0;">'
      + (total > 1 ? '0 / ' + total : '')
      + '</p>'
      + '</div>';

    document.body.appendChild(overlay);
  }

  function updateOverlayCounter(current, total) {
    var el = document.querySelector('.bricktopia-pc-counter');
    if (el && total > 1) {
      el.textContent = current + ' / ' + total;
    }
  }

  // =============================================
  // ГОЛОВНА ЛОГІКА
  // =============================================

  var RELOAD_GUARD_KEY = 'bricktopia_price_check_done';
  var RELOAD_GUARD_TTL = 30000;

  async function runPriceCheck() {
    if (DISABLED) {
      console.log('💰 Price Check: DISABLED (kill switch in bricktopia-price-check.js)');
      return;
    }

    var cart = window.BricktopiaCart_data || null;
    if (!cart || !cart.items || cart.items.length === 0) {
      console.log('💰 Price Check: кошик порожній або дані ще не завантажені');
      return;
    }

    var lastRun = parseInt(sessionStorage.getItem(RELOAD_GUARD_KEY) || '0');
    if (Date.now() - lastRun < RELOAD_GUARD_TTL) {
      console.log('💰 Price Check: вже виконано ' + Math.round((Date.now() - lastRun) / 1000) + 'с тому, пропускаємо');
      return;
    }

    console.log('%c💰 Bricktopia Price Check', 'font-size: 14px; font-weight: bold; color: #8B4513;');
    console.log('━'.repeat(60));

    var items = cart.items;
    var results = [];

    // =============================
    // ФАЗА 1: перевірка всіх цін
    // =============================
    var priceChecks = []; // {item, config, configType, apiType, apiId, cartPrice, apiPrice, diff, props, itemTitle, currentVariantId}

    for (var i = 0; i < items.length; i++) {
      var item = items[i];

      var jsonResult = findJsonConfig(item);
      if (!jsonResult) continue;

      var config = jsonResult.config;
      var configType = jsonResult.configType;
      var apiType = getApiType(configType);
      var apiId = getApiId(config, configType);

      if (apiId === null || apiId === undefined) {
        console.warn('💰 [' + configType + '] Немає ID — пропускаємо');
        continue;
      }

      var cartPrice = item.price / 100;
      var itemTitle = item.product_title || item.title || '???';
      var currentVariantId = item.variant_id;
      var props = item.properties || {};

      // Вивід properties
      console.log('💰 Перевіряємо: [' + apiType + '] #' + apiId + ' (' + itemTitle + ')');
      var builderConfig = props._builder_config || props.builder_config || null;
      var previewUrl = props._preview_url || props.preview_url || null;
      var figureConfigId = props._figure_config_id || props.figure_config_id || null;
      console.log('  📦 Properties:');
      if (builderConfig) {
        try {
          var parsed = typeof builderConfig === 'string' ? JSON.parse(builderConfig) : builderConfig;
          console.log('     builder_config:', parsed);
        } catch (e) {
          console.log('     builder_config (raw):', builderConfig);
        }
      }
      if (previewUrl) console.log('     preview_url:', previewUrl);
      if (figureConfigId) console.log('     figure_config_id:', figureConfigId);

      try {
        var data = await fetchPrice(apiType, apiId);

        if (!data.success) {
          console.warn('  ⚠️ API повернув success: false', data);
          results.push({ item: itemTitle, type: apiType, id: apiId, status: 'api-error', cartPrice: cartPrice });
          continue;
        }

        var apiPrice = data.price;
        var diff = apiPrice - cartPrice;

        if (diff === 0) {
          console.log('  %c✅ Ціна збігається: ' + cartPrice + '₴ = ' + apiPrice + '₴', 'color: #16a34a; font-weight: 600;');
          results.push({ item: itemTitle, type: apiType, id: apiId, status: 'same', cartPrice: cartPrice, apiPrice: apiPrice, diff: 0, currentVariantId: currentVariantId, newVariantId: null });
          continue;
        }

        var icon = diff > 0 ? '🔺' : '🔻';
        var color = diff > 0 ? '#dc2626' : '#2563eb';
        var sign = diff > 0 ? '+' : '';
        console.log(
          '  %c' + icon + ' Ціна ' + (diff > 0 ? 'ЗРОСЛА' : 'ЗНИЗИЛАСЬ') +
          ': в кошику ' + cartPrice + '₴ → актуальна ' + apiPrice + '₴ (' + sign + diff + '₴)',
          'color: ' + color + '; font-weight: 700;'
        );

        // TODO: поки не оновлюємо якщо ціна зросла
        if (diff > 0) {
          console.log('  ⏭️ Ціна зросла — пропускаємо оновлення (поки вимкнено)');
          results.push({ item: itemTitle, type: apiType, id: apiId, status: 'increased', cartPrice: cartPrice, apiPrice: apiPrice, diff: diff, currentVariantId: currentVariantId, newVariantId: null, addedToCart: false, oldRemoved: false, skipped: true });
          continue;
        }

        // Потрібно оновити
        priceChecks.push({ item: item, config: config, configType: configType, apiType: apiType, apiId: apiId, cartPrice: cartPrice, apiPrice: apiPrice, diff: diff, props: props, itemTitle: itemTitle, currentVariantId: currentVariantId });

      } catch (err) {
        console.error('  ❌ Помилка для [' + apiType + '] #' + apiId + ':', err.message);
        results.push({ item: itemTitle, type: apiType, id: apiId, status: 'fetch-error', cartPrice: cartPrice, error: err.message, currentVariantId: currentVariantId });
      }
    }

    // =============================
    // ФАЗА 2: оновлення кошика
    // =============================
    var toUpdateTotal = priceChecks.length;

    if (toUpdateTotal > 0) {
      console.log('━'.repeat(60));
      console.log('%c🔄 Оновлюємо ' + toUpdateTotal + ' товар(ів)...', 'color: #7c3aed; font-weight: 700;');
      showLoadingOverlay(toUpdateTotal);
    }

    for (var j = 0; j < priceChecks.length; j++) {
      var pc = priceChecks[j];
      var updatedSoFar = j + 1;
      updateOverlayCounter(updatedSoFar, toUpdateTotal);

      console.log('  [' + updatedSoFar + '/' + toUpdateTotal + '] ' + pc.itemTitle + ' — ' + pc.cartPrice + '₴ → ' + pc.apiPrice + '₴');

      var productType = getProductType(pc.configType);
      var newVariantId = null;
      var variantCreated = null;
      var addedToCart = false;
      var oldRemoved = false;

      // Крок 1: знайти variant
      try {
        var variantData = await fetchVariantByPrice(pc.apiPrice, productType);

        if (variantData && variantData.variant) {
          newVariantId = variantData.variant.id;
          variantCreated = !!variantData.created;
          var numericId = extractNumericId(newVariantId);
          console.log(
            '  %c🔄 Variant ' + numericId + (variantCreated ? ' (новий)' : ' (існуючий)'),
            'color: #7c3aed; font-weight: 600;'
          );
        } else {
          console.warn('  ⚠️ variant-by-price не повернув variant — пропускаємо товар:', variantData);
          results.push({ item: pc.itemTitle, type: pc.apiType, id: pc.apiId, status: 'decreased', cartPrice: pc.cartPrice, apiPrice: pc.apiPrice, diff: pc.diff, currentVariantId: pc.currentVariantId, newVariantId: null, addedToCart: false, oldRemoved: false, variantError: 'no variant in response' });
          continue;
        }
      } catch (variantErr) {
        console.error('  ❌ Помилка variant-by-price: ' + variantErr.message + ' — пропускаємо товар');
        results.push({ item: pc.itemTitle, type: pc.apiType, id: pc.apiId, status: 'decreased', cartPrice: pc.cartPrice, apiPrice: pc.apiPrice, diff: pc.diff, currentVariantId: pc.currentVariantId, newVariantId: null, addedToCart: false, oldRemoved: false, variantError: variantErr.message });
        continue;
      }

      // Крок 2: додати новий товар
      try {
        var numericNewId = extractNumericId(newVariantId);

        var newProperties = {};
        for (var propKey in pc.props) {
          if (pc.props.hasOwnProperty(propKey)) {
            newProperties[propKey] = pc.props[propKey];
          }
        }

        // Записуємо price_before і нову ціну в builder_config.
        // Пишемо назад у ТОЙ САМИЙ ключ, що був у рядку (_builder_config — нове
        // імʼя, builder_config — старі кошики).
        var bcKey = newProperties._builder_config ? '_builder_config'
          : (newProperties.builder_config ? 'builder_config' : null);
        if (bcKey) {
          try {
            var bc = typeof newProperties[bcKey] === 'string'
              ? JSON.parse(newProperties[bcKey])
              : newProperties[bcKey];
            bc.price_before = pc.cartPrice;
            bc.price = pc.apiPrice;
            newProperties[bcKey] = JSON.stringify(bc);
          } catch (e) { /* залишаємо як є */ }
        }

        var addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: Number(numericNewId),
            quantity: pc.item.quantity,
            properties: newProperties
          })
        });

        if (!addResponse.ok) {
          var errBody = await addResponse.json().catch(function() { return {}; });
          console.error('  ❌ Помилка додавання:', addResponse.status, errBody);
          results.push({ item: pc.itemTitle, type: pc.apiType, id: pc.apiId, status: 'decreased', cartPrice: pc.cartPrice, apiPrice: pc.apiPrice, diff: pc.diff, currentVariantId: pc.currentVariantId, newVariantId: newVariantId, addedToCart: false, oldRemoved: false });
          continue;
        }

        addedToCart = true;
        console.log('  %c✅ Додано! ' + numericNewId + ' × ' + pc.item.quantity + ' (' + pc.apiPrice + '₴)', 'color: #16a34a; font-weight: 700;');

        // Крок 3: видалити старий
        var removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: pc.item.key, quantity: 0 })
        });

        if (removeResponse.ok) {
          oldRemoved = true;
          console.log('  %c🗑️ Старий видалено (' + pc.currentVariantId + ')', 'color: #9333ea; font-weight: 600;');
        } else {
          console.error('  ❌ Не вдалося видалити старий:', removeResponse.status);
        }

      } catch (addErr) {
        console.error('  ❌ Помилка оновлення кошика:', addErr.message);
      }

      results.push({
        item: pc.itemTitle, type: pc.apiType, id: pc.apiId,
        status: 'decreased', cartPrice: pc.cartPrice, apiPrice: pc.apiPrice, diff: pc.diff,
        currentVariantId: pc.currentVariantId, newVariantId: newVariantId,
        variantCreated: variantCreated, productType: productType,
        addedToCart: addedToCart, oldRemoved: oldRemoved
      });
    }

    // =============================================
    // ПІДСУМОК
    // =============================================

    console.log('━'.repeat(60));

    var same = results.filter(function(r) { return r.status === 'same'; }).length;
    var increased = results.filter(function(r) { return r.status === 'increased'; }).length;
    var decreased = results.filter(function(r) { return r.status === 'decreased'; }).length;
    var errors = results.filter(function(r) { return r.status === 'api-error' || r.status === 'fetch-error'; }).length;
    var variantErrors = results.filter(function(r) { return r.variantError; });
    var needsUpdate = results.filter(function(r) { return r.newVariantId != null; });
    var addedCount = results.filter(function(r) { return r.addedToCart === true; }).length;
    var removedCount = results.filter(function(r) { return r.oldRemoved === true; }).length;

    console.log(
      '💰 Підсумок: ' + results.length + ' товарів | ' +
      '✅ ' + same + ' збіг | ' +
      '🔺 ' + increased + ' зросла | ' +
      '🔻 ' + decreased + ' знизилась | ' +
      '❌ ' + errors + ' помилок'
    );

    if (addedCount > 0) {
      console.log(
        '%c🛒 Додано: ' + addedCount + ' | 🗑️ Видалено: ' + removedCount,
        'color: #16a34a; font-weight: 700;'
      );
    }

    if (needsUpdate.length > 0) {
      console.log('%c🔄 Оновлено ' + needsUpdate.length + ' товар(ів):', 'color: #7c3aed; font-weight: 700;');
      needsUpdate.forEach(function(r) {
        var newId = extractNumericId(r.newVariantId);
        var status = r.addedToCart && r.oldRemoved ? '✅' : r.addedToCart ? '⚠️ старий не видалено' : '❌ не додано';
        console.log(
          '   ' + r.item + ': ' + r.currentVariantId + ' → ' + newId +
          ' (' + r.cartPrice + '₴ → ' + r.apiPrice + '₴) ' + status
        );
      });
    } else if (errors === 0 && variantErrors.length === 0) {
      console.log('%c✅ Всі ціни актуальні, оновлення не потрібне', 'color: #16a34a; font-weight: 600;');
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    }

    if (variantErrors.length > 0) {
      console.log('%c⚠️ Не вдалося оновити ' + variantErrors.length + ' товар(ів) — помилка variant-by-price:', 'color: #dc2626; font-weight: 600;');
      variantErrors.forEach(function(r) {
        console.log('   ' + r.item + ': ' + r.cartPrice + '₴ → ' + r.apiPrice + '₴ | Помилка: ' + r.variantError);
      });
    }

    // Зберігаємо результати
    window.BricktopiaCartPriceCheck = {
      results: results,
      needsUpdate: needsUpdate,
      summary: { total: results.length, same: same, increased: increased, decreased: decreased, errors: errors, variantErrors: variantErrors.length, addedToCart: addedCount, removed: removedCount },
      timestamp: new Date().toISOString()
    };

    console.log('💰 Результати: window.BricktopiaCartPriceCheck');
    console.log('━'.repeat(60));

    // Якщо були зміни — перезавантажуємо сторінку
    if (addedCount > 0) {
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      console.log('%c🔄 Перезавантаження сторінки...', 'color: #7c3aed; font-weight: 700;');
      setTimeout(function() {
        window.location.reload();
      }, 800);
    }

    return results;
  }

  // =============================================
  // ЗАПУСК
  // =============================================

  function waitAndRun() {
    if (DISABLED) {
      console.log('💰 Price Check: auto-run skipped (DISABLED)');
      return;
    }
    var attempts = 0;
    var maxAttempts = 20;
    var interval = setInterval(function() {
      attempts++;
      var cart = window.BricktopiaCart_data;
      if (cart && cart.items && cart.items.length > 0) {
        clearInterval(interval);
        setTimeout(function() { runPriceCheck(); }, 300);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        console.log('💰 Price Check: cart data не з\'явилася після ' + maxAttempts + ' спроб');
      }
    }, 500);
  }

  if (!DISABLED) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', waitAndRun);
    } else {
      waitAndRun();
    }
  }

  window.BricktopiaRunPriceCheck = runPriceCheck;

})();